import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  MenuItem,
  Menu
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { paymentService } from '../../services';

const PaymentList = () => {
  const navigate = useNavigate();
  
  // State for payments data
  const [payments, setPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination and filtering state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  
  // Fetch payments data
  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Convert page for API (1-based)
      const apiPage = page + 1;
      
      // Prepare filter object
      const filter = {};
      
      if (searchTerm) {
        filter.referenceNumber = searchTerm;
      }
      
      if (statusFilter) {
        filter.status = statusFilter;
      }
      
      if (dateFilter) {
        // Simple date range logic (could be more sophisticated)
        const today = new Date();
        let startDate;
        
        if (dateFilter === '7days') {
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 7);
        } else if (dateFilter === '30days') {
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 30);
        } else if (dateFilter === '90days') {
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 90);
        }
        
        if (startDate) {
          filter.createdAt = { $gte: startDate.toISOString() };
        }
      }
      
      const response = await paymentService.getPayments({
        page: apiPage,
        limit: rowsPerPage,
        sort: '-createdAt', // Sort by newest first
        filter
      });
      
      setPayments(response.payments || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };
  
  // Load payments on initial render and when pagination/filtering changes
  useEffect(() => {
    fetchPayments();
  }, [page, rowsPerPage, searchTerm, statusFilter, dateFilter]);
  
  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle search
  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      setSearchTerm(event.target.value);
      setPage(0);
    }
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  
  // Handle date filter change
  const handleDateFilterChange = (event) => {
    setDateFilter(event.target.value);
    setPage(0);
  };
  
  // Handle action menu open
  const handleActionMenuOpen = (event, paymentId) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedPaymentId(paymentId);
  };
  
  // Handle action menu close
  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedPaymentId(null);
  };
  
  // Handle payment status update
  const handleUpdateStatus = async (status) => {
    if (!selectedPaymentId) return;
    
    try {
      await paymentService.updatePaymentStatus(selectedPaymentId, status);
      fetchPayments(); // Refresh payment list
    } catch (err) {
      setError(`Failed to update payment status: ${err.message}`);
    } finally {
      handleActionMenuClose();
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Format currency
  const formatCurrency = (amount, currency = 'THB') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };
  
  // Handle export payments
  const handleExportPayments = async (format = 'csv') => {
    try {
      setLoading(true);
      
      // Prepare filter for export
      const filter = {};
      if (statusFilter) filter.status = statusFilter;
      if (searchTerm) filter.referenceNumber = searchTerm;
      
      const blob = await paymentService.exportPayments({
        format,
        filter
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments-export-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      setError(`Failed to export payments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Payments
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage payment records and track transaction history
        </Typography>
      </Box>
      
      {/* Action buttons and search */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExportPayments('csv')}
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => handleExportPayments('excel')}
          >
            Export Excel
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            placeholder="Search reference..."
            size="small"
            onKeyPress={handleSearch}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
            }}
          />
          
          {/* Status filter */}
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            SelectProps={{
              native: true,
            }}
            sx={{ minWidth: 120 }}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </TextField>
          
          {/* Date filter */}
          <TextField
            select
            size="small"
            value={dateFilter}
            onChange={handleDateFilterChange}
            SelectProps={{
              native: true,
            }}
            sx={{ minWidth: 120 }}
          >
            <option value="">All Time</option>
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 90 Days</option>
          </TextField>
        </Box>
      </Box>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Payments table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="payments table">
              <TableHead>
                <TableRow>
                  <TableCell>Reference</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        No payments found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment._id}>
                      <TableCell>{payment.referenceNumber || 'N/A'}</TableCell>
                      <TableCell>
                        {payment.customerId && (
                          <Link
                            to={`/admin/customers/${payment.customerId._id}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            {`${payment.customerId.firstName} ${payment.customerId.lastName}`}
                          </Link>
                        )}
                        {!payment.customerId && 'N/A'}
                      </TableCell>
                      <TableCell>{formatCurrency(payment.amount, payment.currency)}</TableCell>
                      <TableCell>{payment.paymentMethod}</TableCell>
                      <TableCell>{formatDate(payment.createdAt)}</TableCell>
                      <TableCell>
                        <Chip
                          label={payment.status}
                          color={getStatusColor(payment.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => navigate(`/admin/payments/${payment._id}`)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Actions">
                            <IconButton
                              onClick={(e) => handleActionMenuOpen(e, payment._id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </CardContent>
      </Card>
      
      {/* Action menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => handleUpdateStatus('paid')}>Mark as Paid</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('pending')}>Mark as Pending</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('failed')}>Mark as Failed</MenuItem>
        <MenuItem onClick={() => handleUpdateStatus('refunded')}>Mark as Refunded</MenuItem>
      </Menu>
    </Container>
  );
};

export default PaymentList;